import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ImageDataLike } from 'gatsby-plugin-image';
import { Image } from '@agria/theme/src/components/Image/Image';
import { HeroContent } from '@agria/theme/src/components/heroContent/HeroContent';
import { Container } from '../container/Container';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import tokens from './tokens';

// Interface

export interface HeroProps {
  image?:
    | {
        image: { localFile: ImageDataLike };
        gatsbyImage?: any;
      }
    | string;
  useHomePageVideo?: boolean;
  superTitle?: React.ReactNode;
  colorCode?: string;
  returnLink?: {
    onClick?: () => void;
    text: string;
    url: string;
  };
  heading?: string;
  description?: string;
  buttonOne?: Array<{
    component: {
      link: {
        label: string;
        target: string | null;
        url: string;
      };
    };
  }>;
  logos?: string;
  trustpilotStars?: boolean;
  heroComponent?: React.ReactNode;
  jumbo?: boolean;
  children?: any;
  heroBodyComponent?: React.ReactNode;
  small?: boolean;
  fullWidth?: boolean;
  compressed?: boolean;
  showAllLogos?: boolean;
  minHeight?: number;
  includeDiscountTerms?: boolean;
  isHomepage?: boolean;
  AGRIA108?: string;
}

export interface StyledHeroProps {
  hasHeroComponent?: boolean;
  jumbo?: boolean;
  borderColor?: ThemeColorsKeys;
  fullWidth?: boolean;
  compressed?: boolean;
  minHeight?: number;
  includeDiscountTerms?: boolean;
}

interface StyledImageProps {
  fullWidth?: boolean;
}

const StyledHero = styled.div<StyledHeroProps>`
  ${({
    theme: { space, media, maxMedia, colors },
    hasHeroComponent,
    fullWidth,
    compressed,
    minHeight,
  }) => {
    const isMobile = `@media (max-width: ${maxMedia.medium})`;
    const isDesktop = `@media (min-width: ${media.medium})`;
    const shouldSetMinHeight = !compressed;
    const shouldAddMargins = !fullWidth && !compressed;
    const paddingValue = hasHeroComponent
      ? `${space.xxLarge} 0 0 0`
      : `${space.xxLarge} 0`;

    return css`
      position: relative;

      ${fullWidth &&
      css`
        padding: ${space.xxLarge} ${space.medium};

        ${isMobile} {
          padding: ${tokens.includeImageOverlay
            ? '0'
            : `${space.xxLarge} ${space.medium}`};

          ${tokens.includeImageOverlay &&
          css`
            display: flex;
            flex-direction: column-reverse;
          `}
        }
      `}

      ${compressed &&
      css`
        padding: 0;
        ol {
          max-width: 1120px;
          margin: 0 auto;
          padding: 0 ${space.fluidxSmallToXLarge};

          @media (max-width: ${maxMedia.medium}) {
            padding: 0;
          }
        }
      `}

      ${isDesktop} {
        padding: ${!compressed && paddingValue};

        ${shouldSetMinHeight &&
        (minHeight || tokens.minHeight) &&
        css`
          min-height: ${minHeight || tokens.minHeight}px;
        `}

        ${shouldAddMargins &&
        css`
          margin: 0 ${space.large};

          &:not(:has(+ section[data-background-color='utilityWhite'])):before {
            content: '';
            position: absolute;
            z-index: -1;
            top: 0;
            left: -${space.large};
            right: -${space.large};
            bottom: 0;
            background: linear-gradient(
              ${colors.utilityWhite} 50%,
              ${colors.utilityBackground} 0
            );
          }
        `}
      }
    `;
  }}
`;

const StyledImage = styled(Image)<StyledImageProps>`
  ${({ theme: { layers, media, colors, maxMedia }, fullWidth }) => css`
    position: ${fullWidth && 'absolute'};
    z-index: ${layers.heroBackgroundImage};
    background-color: ${colors.utilityBackground};

    @media (max-width: ${maxMedia.medium}) {
      position: ${fullWidth &&
      (tokens.includeImageOverlay ? 'relative' : 'absolute')};
      min-height: 13.1875rem; // 211px

      // Double nesting selector increases specificity to override Image component borders
      && img {
        border-radius: 0;
      }
    }

    @media screen and (min-width: ${media.small}) and (max-width: ${maxMedia.xLarge}) {
      img {
        object-position: 70% 50%;
      }
    }

    @media (min-width: ${media.medium}) {
      position: absolute !important;
      inset: 0;

      img {
        object-position: ${tokens.imagePosition};
      }

      ${tokens.includeImageOverlay &&
      css`
        &::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.9) 18%,
            transparent 76%
          );
        }
      `}
    }
  `};
`;

const StyledContainer = styled(Container)`
  ${({ theme: { maxMedia } }) => css`
    @media (max-width: ${maxMedia.medium}) {
      margin: ${tokens.includeImageOverlay ? '0' : '0 auto'};
    }
  `};
`;

const StyledChildren = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    @media (max-width: ${maxMedia.medium}) {
      margin-inline: -${space.medium};
    }
  `};
`;

const StyledVideo = styled.video`
  ${({ theme: { borderRadii, media } }) => css`
    width: 100%;

    @media (min-width: ${media.medium}) {
      position: absolute;
      top: 0;
      height: 100%;
      object-fit: cover;
      z-index: -1;
      border-radius: ${borderRadii.medium};
    }
  `};
`;

const getStyledHeroComponent = (compressed?: boolean) => {
  if (compressed) {
    return styled.div<StyledHeroProps>`
      ${({ theme: { maxMedia } }) => css`
        > div {
          border-radius: 0;

          ol {
            margin: 0 auto;
            max-width: 1120px;
          }
        }

        @media (max-width: ${maxMedia.medium}) {
          max-width: 100%;
          padding: 0;
        }
      `};
    `;
  }
  return styled(Container)<StyledHeroProps>`
    ${({ theme: { maxMedia, media } }) => css`
      @media (max-width: ${maxMedia.medium}) {
        max-width: 100%;
        padding: 0;
        width: 100%;
        order: -1;
      }
      @media (min-width: ${media.medium}) {
        position: absolute;
        bottom: 0;
        left: 50%;
        width: 100%;
        transform: translateX(-50%);
      }
    `};
  `;
};

export const Hero: FC<HeroProps> = ({
  image,
  useHomePageVideo,
  colorCode = 'none',
  returnLink,
  superTitle,
  heading,
  description,
  buttonOne,
  trustpilotStars,
  logos,
  heroComponent,
  jumbo,
  children,
  heroBodyComponent,
  small,
  fullWidth = tokens.heroFullWidth,
  compressed,
  minHeight,
  includeDiscountTerms,
  isHomepage,
  AGRIA108,
  ...rest
}) => {
  const DynamicHeroComponent = getStyledHeroComponent(compressed);
  const posterImage =
    typeof image === 'object' && image?.gatsbyImage?.images?.fallback?.src;

  return (
    <StyledHero
      jumbo={jumbo}
      hasHeroComponent={!!heroComponent}
      {...rest}
      fullWidth={fullWidth}
      compressed={compressed}
      minHeight={minHeight}
      includeDiscountTerms={includeDiscountTerms}
    >
      <StyledContainer>
        {children && <StyledChildren>{children}</StyledChildren>}

        {!children && !compressed && (
          <HeroContent
            colorCode={colorCode}
            returnLink={returnLink}
            superTitle={superTitle}
            heading={heading}
            description={description}
            buttonOne={buttonOne}
            trustpilotStars={trustpilotStars}
            logos={logos}
            jumbo={jumbo}
            heroBodyComponent={heroBodyComponent}
            small={small}
            fullWidth={fullWidth}
            includeDiscountTerms={includeDiscountTerms}
            isHomepage={isHomepage}
          />
        )}
      </StyledContainer>

      {useHomePageVideo && (
        <StyledVideo
          autoPlay
          loop
          muted
          playsInline
          poster={posterImage}
          src="https://agria.uksouth01.umbraco.io/media/51manyk1/homepage-banner-web.mp4"
        />
      )}

      {!useHomePageVideo && image && !compressed && (
        <StyledImage
          fullWidth={fullWidth}
          src={image}
          alt="" // No alt text needed as this is (probably) a decorative image.
          loading="eager"
          roundCorners={fullWidth ? undefined : 'all'}
          aria-hidden="true"
        />
      )}

      {heroComponent && (
        <DynamicHeroComponent jumbo={jumbo}>
          {heroComponent}
        </DynamicHeroComponent>
      )}
    </StyledHero>
  );
};

export default Hero;
